import React, { useState, useMemo, useEffect } from 'react';
import PropTypes from 'prop-types';
import {
  Box,
  Chip,
  Typography,
  IconButton,
  CircularProgress,
  Select,
  MenuItem
} from '@mui/material';
import { 
  MoreVert,
  Person,
  FirstPage,
  LastPage,
  ChevronLeft,
  ChevronRight
} from '@mui/icons-material';
import {
  useReactTable,
  getCoreRowModel,
  getSortedRowModel,
  getPaginationRowModel,
  flexRender
} from '@tanstack/react-table';
import EmployeeActions from './EmployeeActions';
import { useQuery } from '@tanstack/react-query';
import { fetchHierarchyDefinitions } from '../../services/hierarchyService';
import { usePeopleAdmin } from '../../context/PeopleAdminContext';

/**
 * Table component for displaying employee list using TanStack Table v8
 * Matches LocationAdmin table style for consistency
 * @param {Object} props - Component props
 * @param {Array} props.employees - Array of employee records to display
 * @param {boolean} props.loading - Loading state indicator
 * @param {Function} props.onEdit - Handler for edit action
 * @param {Function} props.onDeactivate - Handler for deactivate action
 * @param {Function} props.onReactivate - Handler for reactivate action
 * @param {Function} props.onLinkUser - Handler for link user action
 * @param {Function} props.onUnlinkUser - Handler for unlink user action
 * @param {Function} props.onViewUser - Handler for view user action
 * @returns {React.Component} Employee list table with actions
 */
function EmployeeList({ 
  employees = [], 
  loading = false,
  onEdit,
  onDeactivate,
  onReactivate,
  onLinkUser,
  onUnlinkUser,
  onViewUser
}) {
  const [sorting, setSorting] = useState([{ id: 'name', desc: false }]);
  const [pagination, setPagination] = useState({ pageIndex: 0, pageSize: 10 });
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [assignmentCounts, setAssignmentCounts] = useState({});
  const { clientId } = usePeopleAdmin();

  // Fetch hierarchy definitions
  const { data: hierarchyLevels = [] } = useQuery({
    queryKey: ['hierarchyDefinitions', clientId],
    queryFn: () => fetchHierarchyDefinitions(clientId),
    enabled: !!clientId,
    staleTime: 30 * 60 * 1000,
    cacheTime: 60 * 60 * 1000
  });

  // Calculate assignment counts from employee data
  useEffect(() => {
    if (!employees || employees.length === 0) return;
    
    const counts = {};
    
    // Initialize counts for each employee
    employees.forEach(employee => {
      const levelCounts = {};
      const locationCount = { locations: 0 };
      
      // Initialize hierarchy level counts
      hierarchyLevels.forEach(level => {
        levelCounts[level.levelId] = 0;
      });
      
      // If employee has assignments array
      if (employee.assignments && Array.isArray(employee.assignments)) {
        employee.assignments.forEach(assignment => {
          // Count location assignments
          if (assignment.type === 'location') {
            locationCount.locations++;
          } else if (assignment.type === 'orgUnit' || assignment.orgLevel) {
            // Count org unit assignments by level
            const orgLevel = assignment.orgLevel || 'ORGLEVEL_1';
            if (levelCounts[orgLevel] !== undefined) {
              levelCounts[orgLevel]++;
            }
          }
        });
      } else if (employee.assignmentsByLevel) {
        // If pre-calculated counts are available
        if (employee.assignmentsByLevel.locations !== undefined) {
          locationCount.locations = employee.assignmentsByLevel.locations;
        }
        Object.keys(employee.assignmentsByLevel).forEach(level => {
          if (level !== 'locations' && levelCounts[level] !== undefined) {
            levelCounts[level] = employee.assignmentsByLevel[level];
          }
        });
      }
      
      counts[employee.id] = { ...levelCounts, ...locationCount };
    });
    
    setAssignmentCounts(counts);
  }, [employees, hierarchyLevels]);

  // Define columns matching LocationsTab style
  const columns = useMemo(() => {
    const baseColumns = [
      // Employee Name column
      {
        accessorKey: 'name',
        header: 'Employee Name',
        cell: ({ row }) => (
          <Box display="flex" alignItems="center">
            <Person sx={{ mr: 0.75, color: '#7C3AED', fontSize: 18 }} />
            <Box>
              <Typography variant="body2" fontWeight="medium" sx={{ fontSize: '0.8125rem', lineHeight: 1.2 }}>
                {row.original.lastName}, {row.original.firstName}
              </Typography>
              {row.original.email && (
                <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.75rem', lineHeight: 1.2 }}>
                  {row.original.email}
                </Typography>
              )}
            </Box>
          </Box>
        ),
        size: 280,
        sortingFn: (rowA, rowB) => {
          const nameA = `${rowA.original.lastName}, ${rowA.original.firstName}`.toLowerCase();
          const nameB = `${rowB.original.lastName}, ${rowB.original.firstName}`.toLowerCase();
          return nameA.localeCompare(nameB);
        },
      },
      // Location column
      {
        id: 'locations',
        header: 'Locations',
        cell: ({ row }) => {
          const counts = assignmentCounts[row.original.id] || {};
          const count = counts.locations || 0;
          return (
            <Typography variant="body2" sx={{ fontSize: '0.8125rem', textAlign: 'center', width: '100%' }}>
              {count}
            </Typography>
          );
        },
        size: 90,
      },
    ];

    // Add dynamic columns for each hierarchy level (only if defined in HierarchyDefinition)
    if (hierarchyLevels && hierarchyLevels.length > 0) {
      hierarchyLevels
        .filter(level => level.isUsed !== false) // Only show used levels
        .sort((a, b) => (a.order || 0) - (b.order || 0)) // Sort by order
        .forEach(level => {
          baseColumns.push({
            id: level.levelId,
            header: level.label,
            cell: ({ row }) => {
              const counts = assignmentCounts[row.original.id] || {};
              const count = counts[level.levelId] || 0;
              return (
                <Typography variant="body2" sx={{ fontSize: '0.8125rem', textAlign: 'center', width: '100%' }}>
                  {count}
                </Typography>
              );
            },
            size: Math.max(level.label.length * 8, 100), // Dynamic size based on label length
          });
        });
    }

    // Add remaining columns (Status, RadixDash User, Actions)
    baseColumns.push(
      {
        id: 'status',
        header: 'Status',
        accessorKey: 'isActive',
        cell: ({ row }) => {
          const isActive = row.original.isActive !== false;
          return (
            <Box sx={{ textAlign: 'center', width: '100%' }}>
              <Chip
                label={isActive ? 'Active' : 'Inactive'}
                size="small"
                color={isActive ? 'success' : 'error'}
                variant="outlined"
                sx={{ 
                  fontSize: '0.75rem',
                  height: '22px',
                  '& .MuiChip-label': {
                    px: 1,
                    py: 0
                  }
                }}
              />
            </Box>
          );
        },
        size: 90,
      },
      {
        id: 'systemAccess',
        header: 'RadixDash User',
        accessorKey: 'userId',
        cell: ({ row }) => {
          const hasUser = !!row.original.userId;
          return (
            <Box sx={{ textAlign: 'center', width: '100%' }}>
              <Chip
                label={hasUser ? 'Yes' : 'No'}
                size="small"
                color={hasUser ? 'primary' : 'default'}
                variant="outlined"
                sx={{ 
                  fontSize: '0.75rem',
                  height: '22px',
                  '& .MuiChip-label': {
                    px: 1,
                    py: 0
                  }
                }}
              />
            </Box>
          );
        },
        size: 110,
      },
      {
        id: 'actions',
        header: 'Actions',
        cell: ({ row }) => (
          <IconButton
            size="small"
            onClick={(e) => {
              e.stopPropagation();
              handleActionsClick(e, row.original);
            }}
            sx={{ p: 0.5 }}
          >
            <MoreVert fontSize="small" />
          </IconButton>
        ),
        size: 80,
      },
    );

    return baseColumns;
  }, [hierarchyLevels, assignmentCounts]);

  // Initialize TanStack Table
  const table = useReactTable({
    data: employees,
    columns,
    state: {
      sorting,
      pagination,
    },
    onSortingChange: setSorting,
    onPaginationChange: setPagination,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
  });

  const handleActionsClick = (event, employee) => {
    setAnchorEl(event.currentTarget);
    setSelectedEmployee(employee);
  };

  const handleActionsClose = () => {
    setAnchorEl(null);
    setSelectedEmployee(null);
  };

  const handleRowClick = (employee) => {
    if (onEdit) {
      onEdit(employee);
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  if (employees.length === 0) {
    return (
      <Box p={4} textAlign="center">
        <Typography color="text.secondary">
          No employees found. Add your first employee to get started.
        </Typography>
      </Box>
    );
  }

  return (
    <>
      <Box sx={{ width: '100%', overflowX: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            {table.getHeaderGroups().map(headerGroup => (
              <tr key={headerGroup.id}>
                {headerGroup.headers.map(header => (
                  <th
                    key={header.id}
                    style={{
                      width: header.getSize(),
                      padding: '8px 12px',
                      textAlign: (header.column.id === 'status' || 
                                 header.column.id === 'systemAccess' || 
                                 header.column.id === 'locations' ||
                                 hierarchyLevels.some(level => level.levelId === header.column.id)) ? 'center' : 'left',
                      borderBottom: '2px solid #e0e0e0',
                      backgroundColor: '#f5f5f5',
                      fontWeight: 600,
                      fontSize: '0.8125rem',
                      color: '#424242'
                    }}
                  >
                    {header.isPlaceholder
                      ? null
                      : flexRender(
                          header.column.columnDef.header,
                          header.getContext()
                        )}
                  </th>
                ))}
              </tr>
            ))}
          </thead>
          <tbody>
            {table.getRowModel().rows.length > 0 ? (
              table.getRowModel().rows.map(row => (
                <tr 
                  key={row.id}
                  onClick={() => handleRowClick(row.original)}
                  style={{
                    borderBottom: '1px solid #e0e0e0',
                    backgroundColor: row.getIsSelected() ? '#f5f5f5' : 'white',
                    cursor: 'pointer',
                    transition: 'background-color 0.2s'
                  }}
                  onMouseEnter={(e) => {
                    if (!row.getIsSelected()) {
                      e.currentTarget.style.backgroundColor = '#fafafa';
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (!row.getIsSelected()) {
                      e.currentTarget.style.backgroundColor = 'white';
                    }
                  }}
                >
                  {row.getVisibleCells().map(cell => (
                    <td
                      key={cell.id}
                      style={{
                        width: cell.column.getSize(),
                        padding: '6px 12px',
                        fontSize: '0.8125rem'
                      }}
                    >
                      {flexRender(cell.column.columnDef.cell, cell.getContext())}
                    </td>
                  ))}
                </tr>
              ))
            ) : (
              <tr>
                <td 
                  colSpan={table.getAllColumns().length}
                  style={{
                    padding: '32px',
                    textAlign: 'center',
                    color: '#666'
                  }}
                >
                  No employees found
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </Box>
      
      {/* Pagination Controls - Matching LocationsTab style */}
      <Box 
        sx={{ 
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: 'space-between',
          mt: 2,
          pt: 2,
          borderTop: '1px solid #e0e0e0'
        }}
      >
        <Box display="flex" alignItems="center" gap={2}>
          <Typography variant="body2" color="text.secondary">
            Rows per page:
          </Typography>
          <Select
            size="small"
            value={table.getState().pagination.pageSize}
            onChange={(e) => {
              table.setPageSize(Number(e.target.value));
            }}
            sx={{ minWidth: 70 }}
          >
            <MenuItem value={10}>10</MenuItem>
            <MenuItem value={25}>25</MenuItem>
            <MenuItem value={50}>50</MenuItem>
            <MenuItem value={100}>100</MenuItem>
          </Select>
          <Typography variant="body2" color="text.secondary">
            {table.getState().pagination.pageIndex * table.getState().pagination.pageSize + 1}
            -
            {Math.min(
              (table.getState().pagination.pageIndex + 1) * table.getState().pagination.pageSize,
              table.getFilteredRowModel().rows.length
            )}{' '}
            of {table.getFilteredRowModel().rows.length} rows
          </Typography>
        </Box>
        
        <Box display="flex" alignItems="center" gap={1}>
          <IconButton
            size="small"
            onClick={() => table.setPageIndex(0)}
            disabled={!table.getCanPreviousPage()}
          >
            <FirstPage />
          </IconButton>
          <IconButton
            size="small"
            onClick={() => table.previousPage()}
            disabled={!table.getCanPreviousPage()}
          >
            <ChevronLeft />
          </IconButton>
          <Typography variant="body2" sx={{ mx: 2 }}>
            Page {table.getState().pagination.pageIndex + 1} of{' '}
            {table.getPageCount()}
          </Typography>
          <IconButton
            size="small"
            onClick={() => table.nextPage()}
            disabled={!table.getCanNextPage()}
          >
            <ChevronRight />
          </IconButton>
          <IconButton
            size="small"
            onClick={() => table.setPageIndex(table.getPageCount() - 1)}
            disabled={!table.getCanNextPage()}
          >
            <LastPage />
          </IconButton>
        </Box>
      </Box>

      {/* Actions menu */}
      {selectedEmployee && (
        <EmployeeActions
          anchorEl={anchorEl}
          employee={selectedEmployee}
          onClose={handleActionsClose}
          onEdit={onEdit}
          onDeactivate={onDeactivate}
          onReactivate={onReactivate}
          onLinkUser={onLinkUser}
          onUnlinkUser={onUnlinkUser}
          onViewUser={onViewUser}
        />
      )}
    </>
  );
}

EmployeeList.propTypes = {
  employees: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.string.isRequired,
    firstName: PropTypes.string.isRequired,
    lastName: PropTypes.string.isRequired,
    email: PropTypes.string,
    employmentType: PropTypes.string,
    hireDate: PropTypes.string,
    isActive: PropTypes.bool,
    userId: PropTypes.string
  })),
  loading: PropTypes.bool,
  onEdit: PropTypes.func,
  onDeactivate: PropTypes.func,
  onReactivate: PropTypes.func,
  onLinkUser: PropTypes.func,
  onUnlinkUser: PropTypes.func,
  onViewUser: PropTypes.func
};

export default EmployeeList;